import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    useGetDataSourcesQuery,
    useGetRuleSetsQuery,
    useUpdateDataSourceMutation,
    useAuthorRuleSetMutation,
    useConfirmRuleSetMutation,
    useProcessSourceMutation,
    useProcessAllMutation,
    useUploadProcessedFileMutation,
    useUpdateStageStatusMutation,
} from '../../../redux/api/compassPortal';
import { StatusPill, errMsg, downloadFile } from './bits';
import SourceDetail from './SourceDetail';

// Data Processing workspace — the second stage. Shows collected sources; each can
// be processed (run the client's approved rules) or have a processed file uploaded,
// with downloads of the original + processed output. Every source runs with ITS
// assigned rule set (a client can have several) — assign one, or have Bravo draft
// one from the file (Model B) and confirm it.

export default function ProcessingWorkspace({ project, onBack, onComplete, refreshSignal }) {
    const pid = project.project_id;
    const [detailFor, setDetailFor] = useState(null); // source open in the MS detail drawer
    const token = useSelector((state) => state.oauth.auth0Token);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [gateMsg, setGateMsg] = useState(null);
    const [authoringId, setAuthoringId] = useState(null); // source Bravo is drafting for
    const [review, setReview] = useState(null); // { source, rule_id, version, summary, rule_set }
    const [pollProc, setPollProc] = useState(false); // poll sources while a process job runs

    const { data: sources, error: loadError, refetch: refetchSources } =
        useGetDataSourcesQuery({ projectId: pid }, { pollingInterval: pollProc ? 3000 : 0 });
    useEffect(() => {
        setPollProc((sources || []).some((x) => x.process_status === 'running' || x.process_status === 'queued'));
    }, [sources]);
    const procElapsed = (t) => {
        if (!t) return '';
        const ms = Date.now() - new Date(t).getTime();
        if (!(ms > 0)) return '';
        const m = Math.floor(ms / 60000), sec = Math.floor((ms % 60000) / 1000);
        return m ? `${m}m ${sec}s` : `${sec}s`;
    };
    const { data: ruleSetsData, refetch: refetchRuleSets } =
        useGetRuleSetsQuery({ clientName: project.client_name });
    const allRuleSets = ruleSetsData?.rule_sets || [];
    // Confirmed rule sets are the assignable ones.
    const ruleSets = allRuleSets.filter((rs) => (rs.versions || []).some((v) => v.status === 'confirmed'));

    const [updateSource] = useUpdateDataSourceMutation();
    const [authorRules] = useAuthorRuleSetMutation();
    const [confirmRules] = useConfirmRuleSetMutation();
    const [processSource] = useProcessSourceMutation();
    const [processAll] = useProcessAllMutation();
    const [uploadProcessedFile] = useUploadProcessedFileMutation();
    const [updateStageStatus, { isLoading: completing }] = useUpdateStageStatusMutation();

    // Re-fetch after Bravo acts (refreshSignal bumps) — no full remount.
    useEffect(() => { if (refreshSignal) { refetchSources(); refetchRuleSets(); } /* eslint-disable-next-line */ }, [refreshSignal]);

    // Assign which rule set processes a source (a client can have several).
    const assignRuleSet = async (s, ruleSetId) => {
        setBusyId(s.source_id); setError(null);
        try { await updateSource({ projectId: pid, sourceId: s.source_id, rule_set_id: ruleSetId }).unwrap(); }
        catch (e) { setError(`Couldn't assign the rule set for ${s.name} — ${plainProcessError(e)}.`); }
        finally { setBusyId(null); }
    };

    // A source assigned to a rule set that has no confirmed version yet = a draft
    // awaiting the human sign-off. Returns { rule_id, version } or null.
    const draftFor = (s) => {
        if (!s.rule_set_id) return null;
        const rs = allRuleSets.find((r) => r.rule_id === s.rule_set_id);
        if (!rs || (rs.versions || []).some((v) => v.status === 'confirmed')) return null;
        const latest = Math.max(1, ...(rs.versions || []).map((v) => v.version || 1));
        return { rule_id: rs.rule_id, version: latest };
    };

    // Model B: Bravo drafts the rules from the source's uploaded file. The result is
    // a DRAFT — the review drawer's confirm is the human gate.
    const draftWithBravo = async (s) => {
        setAuthoringId(s.source_id); setError(null);
        try {
            const r = await authorRules({ projectId: pid, sourceId: s.source_id }).unwrap();
            setReview({ source: s, ...r });
        } catch (e) { setError(`Bravo couldn't draft rules for ${s.name} — ${errMsg(e)}`); }
        finally { setAuthoringId(null); }
    };
    const confirmDraft = async () => {
        if (!review) return;
        setError(null);
        try {
            await confirmRules({ clientName: project.client_name, ruleId: review.rule_id, version: review.version || 1 }).unwrap();
            setReview(null);
        } catch (e) { setError(`Couldn't confirm the rules — ${errMsg(e)}`); }
    };

    // Turn a raw upstream error into a plain line — never show a status code to a
    // non-technical user. The backend's own detail (already plain) wins.
    const plainProcessError = (e) => {
        const detail = e?.data?.detail;
        if (detail) return String(detail).replace(/\.$/, '');
        const status = e?.status || (String(e?.message || '').match(/\b(4\d\d|5\d\d)\b/) || [])[0];
        if (String(status) === '409') return "the client's file rules haven't been approved yet";
        if (String(status) === '404') return "there's no file attached to process yet";
        return "the file couldn't be read cleanly";
    };

    const runProcess = async (s) => {
        setBusyId(s.source_id); setError(null);
        try { await processSource({ projectId: pid, sourceId: s.source_id }).unwrap(); }
        catch (e) { setError(`We couldn't process ${s.name} — ${plainProcessError(e)}. Sort it in Data Collection and try again.`); }
        finally { setBusyId(null); }
    };
    const uploadProcessed = async (s, file) => {
        if (!file) return;
        setBusyId(s.source_id);
        try { await uploadProcessedFile({ projectId: pid, sourceId: s.source_id, file }).unwrap(); }
        catch (e) { setError(`We couldn't take that processed file for ${s.name} — ${plainProcessError(e)}.`); }
        finally { setBusyId(null); }
    };

    const download = async (s, kind) => {
        try { await downloadFile(token, pid, s.source_id, kind, kind === 'processed' ? s.processed_file_name : s.file_name); }
        catch { setError(`Couldn't download that file for ${s.name}.`); }
    };

    if (loadError && !sources) return <div className="cp-empty">Couldn't load — {errMsg(loadError)}</div>;
    if (!sources) return <div className="cp-empty"><span className="cp-spin" /></div>;

    // Only collected sources are processable.
    const collected = sources.filter((s) => s.collected || s.file_url);
    const processed = collected.filter((s) => s.processed).length;
    const pct = collected.length ? Math.round((processed / collected.length) * 100) : 0;
    const canComplete = collected.length > 0 && processed === collected.length;

    const completeStage = async () => {
        setGateMsg(null);
        try {
            await updateStageStatus({ projectId: pid, stage_id: 'data-processing', status: 'complete' }).unwrap();
            onComplete?.('data-processing', 'data-validation');
        } catch (e) { setGateMsg(errMsg(e)); }
    };

    return (
        <>
            <button className="cp-back-btn" onClick={onBack}>← Back to projects</button>
            <div className="cp-toolbar">
                <div style={{ minWidth: 240 }}>
                    <p className="cp-section-label" style={{ marginBottom: 6 }}>{processed} of {collected.length} sources processed</p>
                    <div className="cp-progress"><span style={{ width: `${pct}%` }} /></div>
                </div>
                <button className="cp-btn cp-btn--sm cp-btn--ghost" disabled={pollProc}
                    onClick={async () => {
                        setError(null);
                        try { await processAll({ projectId: pid }).unwrap(); }
                        catch (e) { setError(errMsg(e)); }
                    }}>Process all</button>
                <button className="cp-btn cp-btn--primary" disabled={!canComplete || completing} onClick={completeStage}>
                    {completing ? 'Completing…' : 'Mark processing complete →'}
                </button>
            </div>
            {error && <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>{error}</p>}
            {gateMsg && <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>{gateMsg}</p>}

            {collected.length === 0 ? (
                <div className="cp-empty">No collected sources to process yet. Finish Data Collection first.</div>
            ) : (
                <div className="cp-table-wrap">
                    <table className="cp-table">
                        <thead><tr><th>Source</th><th>Original</th><th>Rule set</th><th>Status</th><th>Processed file</th><th /></tr></thead>
                        <tbody>
                            {collected.map((s) => (
                                <tr key={s.source_id}>
                                    <td>
                                        <div className="cp-src-name cp-src-open" role="button" tabIndex={0}
                                            onClick={() => setDetailFor(s)}
                                            onKeyDown={(e) => e.key === 'Enter' && setDetailFor(s)}>{s.name}</div>
                                        {s.data_type ? <div className="cp-src-note">{s.data_type}</div> : null}
                                        {(s.process_status === 'running' || s.process_status === 'queued') && (
                                            <div className="cp-pullbar" title="Running the confirmed rules">
                                                <div className="cp-pullbar-track cp-procbar"><span /></div>
                                                <span className="cp-src-note">{s.process_status === 'queued' ? 'queued' : `processing · ${procElapsed(s.process_started_at)}`}</span>
                                            </div>
                                        )}
                                        {s.process_status === 'failed' && s.process_error
                                            ? <div className="cp-src-note" style={{ color: 'var(--co-neg)' }}>{s.process_error}</div> : null}
                                        {s.processing_issue_status === 'open' && s.processing_issue
                                            ? <div className="cp-src-note" style={{ color: 'var(--co-neg)' }}>{s.processing_issue}</div> : null}
                                    </td>
                                    <td>{s.file_url
                                        ? <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                                            <div className="cp-src-note" title={s.file_name}>
                                                {s.source_origin === 'warehouse' ? 'warehouse · ' : ''}{s.file_name || 'file'}
                                                {s.rows ? ` · ${Number(s.rows).toLocaleString()} rows` : ''}
                                            </div>
                                            <button className="cp-file-link" onClick={() => download(s, 'original')}>Download</button>
                                        </div>
                                        : '—'}</td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                                            {draftFor(s) ? (
                                                <button className="cp-btn cp-btn--sm cp-btn--ghost" onClick={() => setReview({ source: s, ...draftFor(s) })}>
                                                    Review draft rules…
                                                </button>
                                            ) : (ruleSets.length > 1 || (!s.rule_set_id && (allRuleSets.length > 1))) ? (
                                                <select className="cp-rule-select" value={s.rule_set_id || ''} disabled={busyId === s.source_id}
                                                    onChange={(e) => assignRuleSet(s, e.target.value)}>
                                                    <option value="">Choose…</option>
                                                    {ruleSets.map((rs) => <option key={rs.rule_id} value={rs.rule_id}>{rs.rule_id}</option>)}
                                                </select>
                                            ) : s.rule_set_id || ruleSets.length === 1 ? (
                                                <span className="cp-sub" style={{ fontSize: 12 }}>{s.rule_set_id || ruleSets[0].rule_id}</span>
                                            ) : null}
                                            {!s.rule_set_id && (
                                                <button className="cp-btn cp-btn--sm cp-btn--ghost" disabled={authoringId === s.source_id}
                                                    onClick={() => draftWithBravo(s)}>
                                                    {authoringId === s.source_id ? 'Bravo is reading the file…' : 'Draft with Bravo'}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td><StatusPill status={s.processed ? 'processed' : (s.processing_issue_status === 'open' ? 'flagged' : 'awaiting')}
                                        label={s.processed ? 'Processed' : (s.processing_issue_status === 'open' ? 'Blocked' : 'Ready to process')} /></td>
                                    <td>{s.processed ? (
                                        <span style={{ display: 'inline-flex', gap: 10 }}>
                                            <button className="cp-file-link" onClick={() => download(s, 'processed')}>Download</button>
                                            {s.drive_processed_url && (
                                                <a className="cp-file-link" href={s.drive_processed_url} target="_blank" rel="noreferrer"
                                                    title="In the Data Portal Drive — Model Inputs">Drive</a>
                                            )}
                                        </span>
                                    ) : '—'}</td>
                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        <button className="cp-btn cp-btn--sm cp-btn--primary"
                                            disabled={busyId === s.source_id || s.process_status === 'running' || s.process_status === 'queued' || s.processing_issue_status === 'open' || ((ruleSets.length > 1 || allRuleSets.length > 1) && !s.rule_set_id) || !!draftFor(s)}
                                            title={draftFor(s) ? 'Confirm the drafted rules first' : ((ruleSets.length > 1 || allRuleSets.length > 1) && !s.rule_set_id) ? 'Assign a rule set first' : undefined}
                                            onClick={() => runProcess(s)}>
                                            {busyId === s.source_id || s.process_status === 'running' || s.process_status === 'queued' ? 'Processing…' : s.processed ? 'Re-run' : 'Process'}
                                        </button>{' '}
                                        <UploadProcessedCell disabled={busyId === s.source_id} onFile={(f) => uploadProcessed(s, f)} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="cp-sub" style={{ marginTop: 12 }}>
                "Process" runs the client's approved rule set over the file server-side. If a source can't be
                processed it's flagged with the reason; resolve it in Data Collection.
            </p>

            {review && (
                <>
                    <div className="cp-drawer-backdrop" onClick={() => setReview(null)} />
                    <div className="cp-drawer" role="dialog" aria-label="Review drafted rules">
                        <h3>Bravo's drafted rules — {review.source.name}</h3>
                        <p className="cp-sub" style={{ marginTop: 0 }}>
                            Bravo read the uploaded file and drafted how to process it. Nothing runs with these
                            rules until you confirm them — that sign-off is recorded against your name.
                        </p>
                        {(review.summary || []).length > 0 ? (
                            <ul style={{ margin: '10px 0 14px', paddingLeft: 18, fontSize: 14, lineHeight: 1.6 }}>
                                {review.summary.map((line, i) => <li key={i}>{line}</li>)}
                            </ul>
                        ) : (
                            <p className="cp-sub">Drafted earlier as <strong>{review.rule_id}</strong> — confirm to use it, or draft again.</p>
                        )}
                        {review.rule_set && (
                            <details style={{ margin: '0 0 14px' }}>
                                <summary className="cp-sub" style={{ cursor: 'pointer' }}>The full recipe (technical)</summary>
                                <pre style={{ fontSize: 11, maxHeight: 240, overflow: 'auto', background: 'rgba(20,22,21,0.04)', padding: 10, borderRadius: 6 }}>
                                    {JSON.stringify(review.rule_set, null, 2)}
                                </pre>
                            </details>
                        )}
                        {error && <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>{error}</p>}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button className="cp-btn cp-btn--primary" onClick={confirmDraft}>✓ Confirm — use these rules</button>
                            <button className="cp-btn cp-btn--ghost" onClick={() => setReview(null)}>Not now</button>
                        </div>
                    </div>
                </>
            )}
            {detailFor && (
                <SourceDetail projectId={pid} clientName={project?.client_name}
                    source={(sources || []).find((x) => x.source_id === detailFor.source_id) || detailFor}
                    onClose={() => setDetailFor(null)} />
            )}
        </>
    );
}

function UploadProcessedCell({ onFile, disabled }) {
    const ref = useRef(null);
    return (
        <>
            <button className="cp-btn cp-btn--sm cp-btn--ghost" disabled={disabled} onClick={() => ref.current?.click()}>Upload processed</button>
            <input ref={ref} type="file" accept=".csv,.xlsx,.xls" hidden onChange={(e) => onFile(e.target.files?.[0])} />
        </>
    );
}
